import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { Customer } from '@/hooks/useCustomers';
import { formatDistanceToNow } from 'date-fns';

interface BulkUploadHistoryModalProps {
  customer: Customer;
  onClose: () => void;
}

interface ProcessedFile {
  id: string;
  file_name: string;
  file_size: number;
  processing_status: string;
  created_at: string;
  updated_at: string;
  amount?: number;
  ai_confidence?: number;
  supplier_name?: string;
  error_details?: any;
}

export const BulkUploadHistoryModal: React.FC<BulkUploadHistoryModalProps> = ({ 
  customer, 
  onClose 
}) => {
  const [files, setFiles] = useState<ProcessedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUploadHistory();
  }, [customer.id]);

  const loadUploadHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load files from bill_upload_queue and bills tables
      const { data: queueData, error: queueError } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('user_id', customer.id)
        .eq('source_type', 'bulk_upload')
        .order('created_at', { ascending: false })
        .limit(50);

      if (queueError) throw queueError;

      // Load completed bills
      const { data: billsData, error: billsError } = await supabase
        .from('bills')
        .select('*')
        .eq('user_id', customer.id)
        .like('file_name', '%')
        .order('created_at', { ascending: false })
        .limit(50);

      if (billsError) throw billsError;

      // Combine and format the data
      const combinedFiles: ProcessedFile[] = [
        ...(queueData || []).map(item => ({
          id: item.id,
          file_name: item.file_name,
          file_size: item.file_size,
          processing_status: item.processing_status,
          created_at: item.created_at,
          updated_at: item.updated_at,
          error_details: typeof item.error_details === 'string' ? item.error_details : JSON.stringify(item.error_details)
        })),
        ...(billsData || []).map(bill => ({
          id: bill.id,
          file_name: bill.file_name || 'Unknown',
          file_size: 0, // Not stored in bills table
          processing_status: bill.processing_status,
          created_at: bill.created_at,
          updated_at: bill.updated_at,
          amount: bill.amount,
          ai_confidence: bill.ai_confidence,
          supplier_name: bill.supplier_name
        }))
      ];

      // Remove duplicates and sort by date
      const uniqueFiles = combinedFiles
        .filter((file, index, self) => 
          index === self.findIndex(f => f.file_name === file.file_name)
        )
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setFiles(uniqueFiles);

    } catch (err) {
      console.error('Failed to load upload history:', err);
      setError('Failed to load upload history');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.toLowerCase().includes('.pdf')) return '📄';
    if (fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)) return '🖼️';
    return '📎';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'pending': return 'secondary';
      case 'processing': return 'default';
      case 'error': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'pending': return '⏳';
      case 'processing': return '⚙️';
      case 'error': return '❌';
      default: return '⏳';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full m-4 p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-foreground">Loading upload history...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full m-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Upload History</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Recent file uploads for {customer.name} • {files.length} files found
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error ? (
            <div className="text-center py-8">
              <div className="text-destructive text-sm">{error}</div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadUploadHistory}
                className="mt-4"
              >
                Try Again
              </Button>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📁</div>
              <p className="text-muted-foreground">No upload history found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Upload some files to see them here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {files.map(file => (
                <div 
                  key={file.id} 
                  className="flex items-center gap-4 p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors"
                >
                  <div className="text-2xl">{getFileIcon(file.file_name)}</div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground truncate">{file.file_name}</span>
                      <Badge variant={getStatusColor(file.processing_status)} className="text-xs">
                        {getStatusIcon(file.processing_status)} {file.processing_status}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-muted-foreground">
                      <span>{formatFileSize(file.file_size)}</span>
                      <span className="mx-2">•</span>
                      <span>{formatDistanceToNow(new Date(file.created_at), { addSuffix: true })}</span>
                      
                      {file.amount && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="text-green-600">${file.amount}</span>
                        </>
                      )}
                      
                      {file.ai_confidence && (
                        <>
                          <span className="mx-2">•</span>
                          <span>{file.ai_confidence}% confidence</span>
                        </>
                      )}
                      
                      {file.supplier_name && (
                        <>
                          <span className="mx-2">•</span>
                          <span>{file.supplier_name}</span>
                        </>
                      )}
                    </div>
                    
                    {file.error_details && (
                      <div className="text-sm text-destructive mt-1">
                        Error: {file.error_details}
                      </div>
                    )}
                    
                    {file.processing_status === 'processing' && (
                      <Progress value={75} className="mt-2 h-1" />
                    )}
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(file.updated_at), { addSuffix: true })}
                    </div>
                    {file.processing_status === 'completed' && (
                      <div className="text-xs text-green-600 mt-1">Processed</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {files.length > 0 && (
              <span>
                {files.filter(f => f.processing_status === 'completed').length} completed, 
                {files.filter(f => f.processing_status === 'processing').length} processing, 
                {files.filter(f => f.processing_status === 'error').length} failed
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadUploadHistory}>
              🔄 Refresh
            </Button>
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};